import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ResumeService } from 'src/services/api/ResumeService';
import createResumeApi from 'src/components/resume/resumeApi';
import {
  defaultValues,
  educations,
  boardOptions,
} from 'src/components/resume/constants';

/**
 * Parse a work-experience date into `{ month, year }` (both padded strings).
 * Accepts the API's `MM/YYYY` form (what we now send), plus ISO `YYYY-MM-DD`
 * (e.g. "2026-02-01") and the shorter `YYYY-MM` for older data. Day is ignored.
 * Returns empty strings when the value is missing/unrecognized.
 */
function parseMonthYear(value) {
  if (!value || typeof value !== 'string') return { month: '', year: '' };
  const trimmed = value.trim();
  // ISO: YYYY-MM-DD or YYYY-MM
  if (trimmed.includes('-')) {
    const [year, month] = trimmed.split('-');
    return { month: month || '', year: year || '' };
  }
  // Legacy: MM/YYYY
  if (trimmed.includes('/')) {
    const [month, year] = trimmed.split('/');
    return { month: month || '', year: year || '' };
  }
  return { month: '', year: '' };
}

// Transform new reference data API structure to match existing component expectations
function transformReferenceData(referenceData) {
  // Handle both response structures:
  // 1. Wrapped: { success, message, data: {...} }
  // 2. Direct: { skills: [...], certifications: [...], ... }
  let data;
  if (referenceData?.data) {
    data = referenceData.data;
  } else if (referenceData) {
    data = referenceData;
  } else {
    return {
      skills: [],
      certifications: [],
      fieldsofstudies: [],
      fieldsofstudieshsc: [],
      states: [],
      locations: {},
      preferedLocations: [],
      resumeUploadProcessLabels: [],
      industries: [],
      jobTypes: [],
      languages: [],
    };
  }

  // Build the form's `states` list + `locations` map ({ stateId: { cities } }).
  // Tolerant of several backend shapes:
  //  - nested: `states_and_cities` / `states` where each state carries `cities`
  //  - split : a top-level `states` list + a separate `cities` list whose items
  //            reference their state via state_id / stateId / state.
  const locations = {};
  const states = [];

  let rawStates = [];
  if (Array.isArray(data.states_and_cities)) {
    rawStates = data.states_and_cities;
  } else if (Array.isArray(data.states)) {
    rawStates = data.states;
  }

  // Group any standalone `cities` array onto its state id.
  const citiesByStateId = {};
  if (Array.isArray(data.cities)) {
    data.cities.forEach((c) => {
      const sid = c?.state_id ?? c?.stateId ?? c?.state ?? null;
      if (sid == null) return;
      if (!citiesByStateId[sid]) citiesByStateId[sid] = [];
      citiesByStateId[sid].push({
        id: c.id,
        name: c.name ?? c.city_name ?? '',
      });
    });
  }

  rawStates.forEach((state) => {
    states.push({ id: state.id, name: state.name ?? state.state_name ?? '' });
    const nestedCities = Array.isArray(state.cities)
      ? state.cities.map((c) => ({
          id: c.id,
          name: c.name ?? c.city_name ?? '',
        }))
      : null;
    locations[state.id] = {
      cities: nestedCities || citiesByStateId[state.id] || [],
    };
  });

  // fields_of_study is now a grouped list:
  //   [{ id, name: "Secondary Education", fields: [{id,name}] },
  //    { id, name: "Higher Education",    fields: [{id,name}] }]
  // Split the inner `fields` into the two lists the education form uses —
  // Secondary → HSC (10th/12th), Higher → degree/diploma/etc. Falls back to
  // treating the array as a flat [{id,name}] list for older payloads.
  const fieldsOfStudyGroups = Array.isArray(data.fields_of_study)
    ? data.fields_of_study
    : [];
  // A group wraps its selectable options in a nested array; tolerate the common
  // key names so a group object never leaks into the dropdown as an option.
  const groupChildren = (g) => {
    const nested = g?.fields ?? g?.sub_fields ?? g?.children ?? g?.options;
    return Array.isArray(nested) ? nested : null;
  };
  const hasFosGroups = fieldsOfStudyGroups.some((g) => groupChildren(g));
  const pickGroupChildren = (matcher) =>
    groupChildren(
      fieldsOfStudyGroups.find((g) => matcher(g) && groupChildren(g))
    ) || [];
  const fieldsOfStudyHsc = hasFosGroups
    ? pickGroupChildren((g) => /secondary/i.test(g?.name || '') || g?.id === 1)
    : fieldsOfStudyGroups;
  const fieldsOfStudyHigher = hasFosGroups
    ? pickGroupChildren((g) => /higher/i.test(g?.name || '') || g?.id === 2)
    : fieldsOfStudyGroups;

  const resumeUploadProcessLabels = Array.isArray(
    data.resume_upload_process_labels
  )
    ? data.resume_upload_process_labels
        .map((row) => ({
          id:
            typeof row?.id === 'number'
              ? row.id
              : Number.parseInt(String(row?.id), 10),
          name: typeof row?.name === 'string' ? row.name : '',
        }))
        .filter((row) => Number.isFinite(row.id) && !Number.isNaN(row.id))
    : [];

  return {
    skills: data.skills || [],
    certifications: data.certifications || [],
    fieldsofstudies: fieldsOfStudyHigher,
    fieldsofstudieshsc: fieldsOfStudyHsc,
    states,
    locations,
    // Preferred-location options: the key was renamed `preferred_work_locations` →
    // `locations`; accept either.
    preferedLocations: data.locations || data.preferred_work_locations || [],
    resumeUploadProcessLabels,
    industries: data.industries || [],
    // Job-type options for the Job Preferences dropdown: [{ id, value }].
    // The reference-data key is `employment_type` (older builds used
    // `employment_types`); accept either.
    jobTypes: data.employment_type || data.employment_types || [],
    languages: data.languages || [],
  };
}

function mapResumeData(response, masterData = {}) {
  if (!response.profile_data) {
    throw new Error('Failed to fetch resume data: profile_data missing');
  }
  const candId = response.candidate_id;
  const registrationId = response.registration_id;
  const profileData = response.profile_data;
  const personalInfo = profileData.personal_information || {};
  const otherDetails = profileData.other_details || {};
  const address = personalInfo.address || {};
  // Map educations for form (raw values/IDs) and labels for preview
  const mappedEducations = profileData.educational_details?.length
    ? profileData.educational_details.map((edu) => {
        const isGPA = edu.score_type === 'GPA';
        const qualificationLabel =
          educations.find((e) => e.value === edu.qualification_type)?.label ||
          edu.qualification_type;
        const boardLabel =
          boardOptions.find((b) => b.value === edu.board_type)?.label ||
          edu.board_type;
        let fieldsOfStudyArray = [];
        if (
          edu.qualification_type === 'TENTH' ||
          edu.qualification_type === 'TWELFTH'
        ) {
          fieldsOfStudyArray = masterData?.fieldsofstudieshsc || [];
        } else {
          fieldsOfStudyArray = masterData?.fieldsofstudies || [];
        }
        const fieldOfStudyVal =
          fieldsOfStudyArray.find((field) => field.id === edu.field_of_study_id)
            ?.name || '';
        return {
          institution: edu.institution || '',
          qualification: edu.qualification_type || '', // raw value for form
          qualificationLabel,
          graduationYear: edu.graduation_year?.toString() || '',
          fieldsOfStudy: edu.field_of_study_id?.toString() || '',
          fieldOfStudyVal,
          gradeType:
            !edu.score_type || edu.score_type === 'MARKS' ? 'marks' : 'gpa',
          maxMarks: !isGPA ? edu.max_score?.toString() || '' : '',
          earnedMarks: !isGPA ? edu.actual_score?.toString() || '' : '',
          maxGPA: isGPA ? edu.max_score?.toString() || '' : '',
          earnedGPA: isGPA ? edu.actual_score?.toString() || '' : '',
          calculatedScore: edu.calculated_score || '',
          board: edu.board_type || '', // raw value for form
          boardLabel,
          university: edu.university || '',
        };
      })
    : defaultValues.educations;

  // Map skills from profile response objects to form state format
  const mappedSkills = (profileData.skills || []).map((s) => ({
    skill_id: s.id,
    ref_skill_id: null,
    skill_name: s.name,
  }));

  const mappedCertifications = (profileData.certifications || []).map((c) => ({
    certification_id: c.id || null,
    ref_certification_id: null,
    name: c.name,
    issuingBody: c.issuing_body || null,
  }));

  // State and city: raw values for form, labels for preview
  // Backend uses *_id fields for state/city; keep fallbacks for older payloads.
  const stateId = address.state_id ?? address.state ?? '';
  const cityId = address.city_id ?? address.city ?? '';
  // Ensure stateLabel is always a string (convert number to string if state not found)
  const stateLabel =
    masterData?.states?.find((s) => s.id === Number(stateId))?.name ||
    (stateId ? String(stateId) : '');
  // Ensure cityLabel is always a string (convert number to string if city not found)
  const cityLabel =
    masterData?.locations?.[stateId]?.cities?.find(
      (c) => c.id === Number(cityId)
    )?.name || (cityId ? String(cityId) : '');

  const parseBooleanish = (v) => v === true || v === 1 || v === '1';
  const noWorkExp = parseBooleanish(personalInfo.no_work_exp);
  const noCertifications = parseBooleanish(personalInfo.no_certifications);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[resume] boolean flags', {
      no_work_exp_raw: personalInfo.no_work_exp,
      no_work_exp_parsed: noWorkExp,
      no_certifications_raw: personalInfo.no_certifications,
      no_certifications_parsed: noCertifications,
    });
  }

  return {
    ...defaultValues,
    registrationId,
    firstname: personalInfo.first_name || '',
    middlename: personalInfo.middle_name || '',
    lastname: personalInfo.last_name || '',
    titleRole: personalInfo.title || '',
    candidateId: candId || '',
    email: personalInfo.email || '',
    emailVerified: personalInfo.is_email_verified === true,
    contact: personalInfo.phone || '',
    dob: personalInfo.dob || '',
    gender: personalInfo.gender ?? '',
    fathersname: personalInfo.father_name || '',
    addressLine1: address.address_line_1 || '',
    street: address.street || '',
    state: stateId,
    stateLabel,
    city: cityId,
    cityLabel,
    zipCode: address.zip_code || '',
    educations: mappedEducations,
    // Preserve a 0 (Immediate) — `|| ''` would coerce it to empty.
    noticePeriod:
      personalInfo.notice_period != null ? personalInfo.notice_period : '',
    workExperiences: profileData.work_experiences?.length
      ? profileData.work_experiences.map((exp) => {
          const { month: fromMonth, year: fromYear } = parseMonthYear(
            exp.from_date
          );
          const { month: toMonth, year: toYear } = parseMonthYear(exp.to_date);
          // Prefer the backend's explicit is_current flag; fall back to an
          // absent end date for older payloads that didn't send it.
          const isCurrent =
            exp.is_current != null ? !!exp.is_current : !toMonth && !toYear;
          return {
            // Backend id for the experience row — kept so per-row CRUD endpoints
            // can target it. null for unsaved/new rows.
            id: exp.id ?? null,
            company: exp.company_name || '',
            role: exp.role || '',
            duties: Array.isArray(exp.duties)
              ? exp.duties.map((d) => ({
                  // Backend id for the individual duty (null for new ones).
                  id: d?.id ?? null,
                  title: d?.title != null ? String(d.title) : '',
                  description:
                    d?.description != null ? String(d.description) : '',
                }))
              : [],
            employment_period: {
              startMonth: fromMonth || '',
              startYear: fromYear || '',
              endMonth: isCurrent ? '' : toMonth || '',
              endYear: isCurrent ? '' : toYear || '',
              present: isCurrent,
            },
          };
        })
      : defaultValues.workExperiences,
    skills: mappedSkills,
    certifications: mappedCertifications,
    currentCTC: personalInfo.current_ctc || '',
    expectedCTC: personalInfo.expected_ctc || '',
    preferredLocations: personalInfo.preferred_work_locations
      ? personalInfo.preferred_work_locations.map((loc) =>
          typeof loc === 'number'
            ? loc.toString()
            : loc.id?.toString() || loc.toString()
        )
      : [],
    professionalSummary: profileData.summary || '',
    noWorkExp,
    noCertifications,
    // API returns these under `preferred_industries`; older payloads used
    // `preferred_industry_ids`. Accept either, and normalize numbers / {id} objects.
    // API returns these under `preferred_industries` as objects
    // ({ industry_id, name }); older payloads used `preferred_industry_ids`
    // (plain IDs). Accept either and normalize to numeric IDs.
    preferredIndustryIds: (
      profileData.job_preferences?.preferred_industries ??
      profileData.job_preferences?.preferred_industry_ids ??
      []
    )
      .map((item) =>
        item && typeof item === 'object'
          ? Number(item.industry_id ?? item.id)
          : Number(item)
      )
      .filter((n) => Number.isFinite(n)),
    workMode: profileData.job_preferences?.work_mode || [],
    companySizes: profileData.job_preferences?.company_size || [],
    openToRelocation: profileData.job_preferences?.open_to_relocation ?? null,
    // Single-select job type — stores the option `value` (e.g. "Full-Time").
    // Multi-select job types (employment_type is an array of option values).
    jobType: Array.isArray(profileData.job_preferences?.employment_type)
      ? profileData.job_preferences.employment_type
      : [],
    languages: (profileData.languages || []).map((l) => {
      const master = (masterData?.languages || []).find(
        (m) => m.id === l.language_id
      );
      return {
        id: l.language_id,
        name: master?.name || '',
        proficiency: l.proficiency || 'CONVERSATIONAL',
        isPrimary: l.is_primary ?? false,
      };
    }),
    masterData: {
      skills: masterData?.skills || [],
      certifications: masterData?.certifications || [],
      fieldsofstudies: masterData?.fieldsofstudies || [],
      fieldsofstudieshsc: masterData?.fieldsofstudieshsc || [],
      states: masterData?.states || [],
      locations: masterData?.locations || [],
      preferedLocations: masterData?.preferedLocations || [],
      industries: masterData?.industries || [],
      languages: masterData?.languages || [],
    },
    updatedAt: personalInfo.updated_at || '',
    otherDetails,
  };
}

/**
 * Reference-data keys the resume dashboard + profile flows don't need, so we
 * exclude them from the fetch. `work_mode` options are hardcoded in the UI.
 * (An exclude list is safer than an include allowlist here: keys not in the
 * documented include set — e.g. `resume_upload_process_labels` — would be
 * dropped by an allowlist and break features.)
 */
export const RESUME_REFERENCE_EXCLUDE = ['work_mode'];

/**
 * Build TanStack Query options for a reference-data fetch scoped by
 * `include` / `exclude` keys. The scope is part of the queryKey so different
 * scopes are cached separately (and identical scopes share one fetch).
 */
export const referenceDataQueryOptions = ({ include, exclude } = {}) => ({
  queryKey: [
    'referenceData',
    {
      include: include ? [...include].sort() : null,
      exclude: exclude ? [...exclude].sort() : null,
    },
  ],
  queryFn: async () => {
    const response = await ResumeService.getReferenceData({ include, exclude });
    return transformReferenceData(response);
  },
  staleTime: 1000 * 60 * 60, // Cache for 1 hour since reference data doesn't change often
});

/** Shared with `useReferenceData` and resume dashboard mount prefetch (same key + fn). */
export const REFERENCE_DATA_QUERY_OPTIONS = referenceDataQueryOptions({
  exclude: RESUME_REFERENCE_EXCLUDE,
});

// Fetch reference data. Pass `referenceDataQueryOptions({ include|exclude })`
// to scope a page/component to just the lists it needs; defaults to the shared
// resume scope.
export function useReferenceData(
  options = REFERENCE_DATA_QUERY_OPTIONS,
  queryOptions = {}
) {
  return useQuery({
    ...options,
    ...queryOptions,
  });
}

// Transformed reference data for a public (token) link — standalone hook for
// pages that need the master lists (states/cities, locations, industries…)
// without loading a full profile. Same key/shape as the query inside
// useResumeProfile.
export function useReferenceDataPublic(token) {
  return useQuery({
    queryKey: ['referenceDataPublic', token],
    queryFn: async () => {
      const response = await ResumeService.getReferenceDataPublic(token, {
        exclude: RESUME_REFERENCE_EXCLUDE,
      });
      return transformReferenceData(response);
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 60,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// Fetch resume profile by token (public)
export function useResumeProfile(token) {
  // For public links, fetch reference data with token
  const referenceDataQuery = useQuery({
    queryKey: ['referenceDataPublic', token],
    queryFn: async () => {
      const response = await ResumeService.getReferenceDataPublic(token, {
        exclude: RESUME_REFERENCE_EXCLUDE,
      });
      return transformReferenceData(response);
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
  });

  const { data: referenceData, isSuccess: isReferenceDataSuccess } =
    referenceDataQuery;

  // Profile query - only enable once when reference data is successfully loaded
  const profileQuery = useQuery({
    queryKey: ['resumeProfile', token], // Only token in queryKey to prevent unnecessary re-runs
    queryFn: async () => {
      const response = await ResumeService.getProfilePublic(token);
      // getProfilePublic now returns the data object directly (same as getProfile)
      return mapResumeData(response, referenceData || {});
    },
    enabled: !!token && isReferenceDataSuccess && !!referenceData, // Only enable when reference data is successfully loaded
    staleTime: Infinity, // Never consider data stale - only fetch once
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: false, // Don't retry on failure to prevent multiple calls
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false, // Don't refetch on reconnect
  });

  return profileQuery;
}

// Fetch resume profile by candidateId (protected)
export function useProtectedResumeProfile(candidateId, options = {}) {
  const { data: referenceData, isLoading: isLoadingReference } =
    useReferenceData();

  return useQuery({
    queryKey: ['protectedResumeProfile', candidateId],
    queryFn: async () => {
      const response = await ResumeService.getProfile(candidateId);
      return mapResumeData(response, referenceData || {});
    },
    enabled: !!candidateId && !isLoadingReference,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

// Update resume profile public
export function useUpdateResumePublic(candidateId, token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => ResumeService.updateProfilePublic(candidateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['resumeProfile', token]);
    },
  });
}

// ── Protected dashboard write mutations ──────────────────────────────────────
// All invalidate the protected profile query (prefix match — there's one active
// candidate profile), replacing the ad-hoc refetch() the dashboard used to call.
const invalidateProtectedProfile = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: ['protectedResumeProfile'] });

/** PATCH a single profile section (certifications NA, work_experience NA, title…). */
export function useUpdateProfileSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, key, data }) =>
      ResumeService.updateProfileSection(candidateId, { key, data }),
    onSuccess: () => invalidateProtectedProfile(queryClient),
  });
}

/** Section-PATCH via the resumeApi abstraction (personal/contact/education/… ). */
export function useUpdateResumeSection(candidateId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, section }) =>
      createResumeApi({ isProtected: true, candidateId }).updateResume(
        payload,
        section
      ),
    onSuccess: () => invalidateProtectedProfile(queryClient),
  });
}

/**
 * Work-experience diff-on-save in one mutation: flush pending duty edits, then
 * create/update rows, then delete removed rows — then invalidate the profile.
 * Callers compute the diff (creates/updates/deleteIds/dutyOps) and pass it in.
 */
export function useSaveWorkExperiences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dutyOps = [], experiences = [], deleteIds = [] }) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const op of dutyOps) {
        if (op.type === 'create') {
          // eslint-disable-next-line no-await-in-loop
          await ResumeService.addWorkExperienceDuty(op.expId, op.body);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await ResumeService.updateWorkExperienceDuty(
            op.expId,
            op.dutyId,
            op.body
          );
        }
      }
      if (experiences.length > 0) {
        await ResumeService.saveWorkExperiences(experiences);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const id of deleteIds) {
        // eslint-disable-next-line no-await-in-loop
        await ResumeService.deleteWorkExperience(id);
      }
    },
    onSuccess: () => invalidateProtectedProfile(queryClient),
  });
}

// ── Live per-duty CRUD (WorkExperienceFields) ────────────────────────────────
// These deliberately do NOT invalidate the profile: a live edit must not refetch
// mid-edit (it would reset the open form). The full refetch happens at Save
// Changes via useSaveWorkExperiences.

/**
 * Add a duty to an existing WE. The add response carries no id, so this also
 * fetches the WE's duties and returns them for the caller to reconcile the id.
 */
export function useAddWorkExperienceDuty() {
  return useMutation({
    mutationFn: async ({ experienceId, duty }) => {
      await ResumeService.addWorkExperienceDuty(experienceId, duty);
      // Reconcile the new id best-effort — the add succeeds even if this fails.
      try {
        return await ResumeService.getWorkExperienceDuties(experienceId);
      } catch {
        return [];
      }
    },
  });
}

export function useUpdateWorkExperienceDuty() {
  return useMutation({
    mutationFn: ({ experienceId, dutyId, duty }) =>
      ResumeService.updateWorkExperienceDuty(experienceId, dutyId, duty),
  });
}

export function useDeleteWorkExperienceDuty() {
  return useMutation({
    mutationFn: ({ experienceId, dutyId }) =>
      ResumeService.deleteWorkExperienceDuty(experienceId, dutyId),
  });
}

/** AI-enhance a duty description. Returns the API response (enhanced text). */
export function useEnhanceWorkExperienceResponsibility() {
  return useMutation({
    mutationFn: (description) =>
      ResumeService.enhanceWorkExperienceResponsibility(description),
  });
}

// ── Misc data-layer hooks ────────────────────────────────────────────────────

/**
 * Look up a 6-digit PIN code's city/state. Enabled only for a valid PIN, and
 * cached forever per PIN (a PIN's location never changes), so re-entering the
 * same PIN doesn't refetch — replacing the ad-hoc guard the effect used.
 */
export function useLocationByPincode(pin) {
  const valid = /^[0-9]{6}$/.test(String(pin || '').trim());
  return useQuery({
    queryKey: ['pincode', pin],
    queryFn: () => ResumeService.getLocationByPincode(pin),
    enabled: valid,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** One-shot resume-PDF export. Returns the PDF blob; no cache to invalidate. */
export function useResumePdf() {
  return useMutation({
    mutationFn: () => ResumeService.getResumePdf(),
  });
}

// ── Public (token-link) dashboard write mutations ────────────────────────────
// Mirror the protected write mutations but invalidate the public profile query
// (`['resumeProfile', token]`), replacing the manual refetch() the public
// dashboard used to call after each save.
const invalidatePublicProfile = (queryClient, token) =>
  queryClient.invalidateQueries({ queryKey: ['resumeProfile', token] });

/** PATCH a single profile section on the public (token) link. */
export function useUpdateProfileSectionPublic(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, key, data }) =>
      ResumeService.updateProfileSectionPublic(
        candidateId,
        { key, data },
        token
      ),
    onSuccess: () => invalidatePublicProfile(queryClient, token),
  });
}

/** Section-PATCH via the resumeApi abstraction on the public (token) link. */
export function useUpdateResumeSectionPublic({ candidateId, token }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, section }) =>
      createResumeApi({ isProtected: false, candidateId, token }).updateResume(
        payload,
        section
      ),
    onSuccess: () => invalidatePublicProfile(queryClient, token),
  });
}

/**
 * Work-experience diff-on-save on the public (token) link — same flush/
 * create-update/delete sequence as useSaveWorkExperiences, against the
 * token-authenticated work-experience endpoints.
 */
export function useSaveWorkExperiencesPublic(token) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dutyOps = [], experiences = [], deleteIds = [] }) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const op of dutyOps) {
        if (op.type === 'create') {
          // eslint-disable-next-line no-await-in-loop
          await ResumeService.addWorkExperienceDutyPublic(
            op.expId,
            op.body,
            token
          );
        } else {
          // eslint-disable-next-line no-await-in-loop
          await ResumeService.updateWorkExperienceDutyPublic(
            op.expId,
            op.dutyId,
            op.body,
            token
          );
        }
      }
      if (experiences.length > 0) {
        await ResumeService.saveWorkExperiencesPublic(experiences, token);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const id of deleteIds) {
        // eslint-disable-next-line no-await-in-loop
        await ResumeService.deleteWorkExperiencePublic(id, token);
      }
    },
    onSuccess: () => invalidatePublicProfile(queryClient, token),
  });
}

// ── Live per-duty CRUD on the public (token) link ────────────────────────────
// Mirror the protected live-duty hooks; deliberately don't invalidate the
// profile (same reasoning — a live edit must not refetch mid-edit).

/** Add a duty to an existing WE on the public link; reconciles the new id. */
export function useAddWorkExperienceDutyPublic(token) {
  return useMutation({
    mutationFn: async ({ experienceId, duty }) => {
      await ResumeService.addWorkExperienceDutyPublic(
        experienceId,
        duty,
        token
      );
      try {
        return await ResumeService.getWorkExperienceDutiesPublic(
          experienceId,
          token
        );
      } catch {
        return [];
      }
    },
  });
}

export function useUpdateWorkExperienceDutyPublic(token) {
  return useMutation({
    mutationFn: ({ experienceId, dutyId, duty }) =>
      ResumeService.updateWorkExperienceDutyPublic(
        experienceId,
        dutyId,
        duty,
        token
      ),
  });
}

export function useDeleteWorkExperienceDutyPublic(token) {
  return useMutation({
    mutationFn: ({ experienceId, dutyId }) =>
      ResumeService.deleteWorkExperienceDutyPublic(experienceId, dutyId, token),
  });
}
